import { Component, OnInit, QueryList, ElementRef, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { PrintService } from '../../services/print.service';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { POSTED_FILTER_OPTIONS } from '../../constants/posted-filter-options';
import {
	TransactionDetailModalComponent,
	GlTransactionDetailData
} from '../account-inquiry/transaction-detail-modal/transaction-detail-modal.component';
import {
	JournalDescriptionEditDialogComponent,
	JournalDescriptionEditDialogData,
	JournalDescriptionEditDialogResult
} from './journal-description-edit-dialog.component';

@Component({
	selector: 'app-journal-inquiry',
	templateUrl: './journal-inquiry.component.html',
	styleUrls: ['./journal-inquiry.component.scss']
})
export class JournalInquiryComponent implements OnInit {
	@ViewChildren('journalTable') journalTables!: QueryList < ElementRef > ;
	journalForm: FormGroup;
	journalData: any = [];
	maxJournalNumber: any = 0;
	color = 'blue';
	exporting: any = false;
	//journalTables: any = [];
	searchQuery = '';
	filteredJournals = [];
	isrunning: any = false;
	reposting = false;
	postedOptions = POSTED_FILTER_OPTIONS;
	attachmentsJournal: any = null;
	journalAttachmentsList: any[] = [];
	attachmentsLoading = false;
	selectedAttachment: any = null;
	selectedAttachmentSafeUrl: SafeResourceUrl | null = null;

	constructor(
		private fb: FormBuilder,
		private glService: GeneralLedgerService,
		private globalSearchService: GlobalSearchService,
		private location: Location,
		private router: Router,
		private printService: PrintService,
		private sanitizer: DomSanitizer,
		private dialog: MatDialog
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.initializeForm();
		this.fetchMaxJournalNumber();
	}

	initializeForm(): void {
		const fromDate = new Date();
		fromDate.setDate(fromDate.getDate() - 30);

		this.journalForm = this.fb.group({
			numberFrom: [1],
			numberTo: [this.maxJournalNumber],
			fromDate: [fromDate],
			toDate: [new Date()],
			posted: ['all']
		});

	}

	fetchMaxJournalNumber(): void {
		this.glService.getMaxJournalNumber().subscribe((maxNumber: any) => {
			this.maxJournalNumber = maxNumber.maxjounral;
			this.journalForm.get('numberTo')?.setValue(this.maxJournalNumber);
		});
	}

	searchJournals(): void {

		if (this.journalForm.valid) {
			this.isrunning = true;
			const criteria = this.journalForm.value;
			this.glService.getJournals(criteria).subscribe((data) => {
				this.journalData = data;
				this.isrunning = false;
				this.filteredJournals = [...this.journalData.data];
			});
		}
	}

	resetForm(): void {
		this.journalForm.reset({
			numberFrom: 1,
			numberTo: this.maxJournalNumber,
			fromDate: new Date(),
			toDate: new Date(),
			posted: 'all'
		});
	}

	filterJournals(): void {
		const query = this.searchQuery.toLowerCase();
		this.filteredJournals = this.journalData.data.filter((journal) => {
			return (
				journal.journalNumber.toLowerCase().includes(query) ||
				journal.date.toLowerCase().includes(query) ||
				journal.transactions.some((transaction) =>
					transaction.accountCode.toLowerCase().includes(query) ||
					transaction.accountName.toLowerCase().includes(query) ||
					transaction.description.toLowerCase().includes(query) ||
					(transaction.tag != null && String(transaction.tag).toLowerCase().includes(query)) ||
					(transaction.tagdescription && transaction.tagdescription.toLowerCase().includes(query)) ||
					transaction.amount.toString().includes(query)
				)
			);
		});
	}

	exportPdf(journalNumber: string): void {
		const journalTable = this.findTableByJournalNumber(journalNumber);
		if (!journalTable) {return;}

		const encoded: string = this.globalSearchService.base64encode(this.stripNoPrint(journalTable));

		const journal = this.journalData.data.find((j) => j.journalNumber === journalNumber);
		if (!journal) {
			console.error(`Journal data not found for journalNumber: ${journalNumber}`);
			return;
		}

		const title = `Journal Number: ${journal.journalNumber}`;
		const subtitle = `Date: ${new Date(journal.date).toLocaleDateString()} Printed ${new Date().toLocaleString()}`;


		const data = {
			content: encoded,
			filename: `Journal_${journalNumber}`,
			landscape: true,
			title: title,
			subtitle: subtitle
		};

		this.exporting = true;
		this.printService.pdf(data).subscribe((result: any) => {
			this.exporting = false;
			this.globalSearchService.downloadPdf(result.content, data.filename);
		});
	}

	exportXls(journalNumber: string): void {
		const journalTable = this.findTableByJournalNumber(journalNumber);
		if (!journalTable) {return;}

		const encoded: string = this.globalSearchService.base64encode(this.stripNoPrint(journalTable));

		const journal = this.journalData.data.find((j) => j.journalNumber === journalNumber);
		if (!journal) {
			console.error(`Journal data not found for journalNumber: ${journalNumber}`);
			return;
		}

		const title = `Journal Number: ${journal.journalNumber}`;
		const subtitle = `Date: ${new Date(journal.date).toLocaleDateString()} Printed ${new Date().toLocaleString()}`;


		const data = {
			content: encoded,
			filename: `Journal_${journalNumber}`,
			title: title,
			subtitle: subtitle
		};

		this.exporting = true;
		this.printService.xls(data).subscribe((result: any) => {
			this.exporting = false;
			this.globalSearchService.downloadXls(result.content, data.filename);
		});
	}

	private stripNoPrint(source: HTMLElement): string {
		const clone = source.cloneNode(true) as HTMLElement;
		clone.querySelectorAll('.no-print').forEach((el) => el.remove());
		return clone.innerHTML;
	}

	private findTableByJournalNumber(journalNumber: string): HTMLElement | null {
		const tableId = `journalTable${journalNumber}`;

		const tableElement = this.journalTables.find(
			(table) => table.nativeElement.id === tableId
		);

		return tableElement ? tableElement.nativeElement : null;
	}


	openEditModal(transaction: any, journal: any): void {
		if (transaction?.counterindex == null) {
			return;
		}
		const amount = parseFloat(String(transaction.amount ?? '0').replace(/,/g, '')) || 0;
		const debit = amount > 0 ? amount : 0;
		const credit = amount < 0 ? -amount : 0;
		const tagref = transaction.tagref != null && transaction.tagref !== ''
			? Number(transaction.tagref)
			: null;

		const data: GlTransactionDetailData = {
			type: 'Journal',
			number: journal?.journalNumber ?? '',
			trandate: journal?.date ?? '',
			debit,
			credit,
			chequeno: '',
			narrative: transaction.description ?? '',
			balance: amount,
			tag: transaction.tagdescription ?? '',
			periodno: '',
			periodname: '',
			counterindex: Number(transaction.counterindex),
			tagref
		};

		this.dialog.open(TransactionDetailModalComponent, {
			width: '480px',
			data
		}).afterClosed().subscribe((result: { saved?: boolean } | undefined) => {
			if (result?.saved) {
				this.searchJournals();
			}
		});
	}

	isReversalEntry(journal: any): boolean {
		const transactions = journal?.transactions || [];
		return transactions.some((tx: any) =>
			(tx.description || '').trim().startsWith('Reversal of Journal #')
		);
	}

	private commonNarrative(journal: any): string {
		const transactions = journal?.transactions || [];
		if (!transactions.length) {
			return '';
		}
		const first = (transactions[0].description ?? '').toString();
		const allMatch = transactions.every((tx: any) => (tx.description ?? '').toString() === first);
		return allMatch ? first : first;
	}

	openEditJournalDescription(journal: any): void {
		if (!journal) {
			return;
		}
		if (this.isReversalEntry(journal)) {
			this.globalSearchService.showNotification(
				'Edit the original journal; the reversal description is auto-derived.',
				'warning',
				'bottom',
				'right'
			);
			return;
		}

		this.glService.checkJournalReversed(journal.journalNumber).subscribe({
			next: (res: any) => {
				const data: JournalDescriptionEditDialogData = {
					journalNumber: journal.journalNumber,
					date: journal.date,
					currentNarrative: this.commonNarrative(journal),
					hasReversal: !!res?.alreadyReversed,
					reversalJournalNumber: res?.reversalJournalNumber ?? null
				};
				this.dialog
					.open(JournalDescriptionEditDialogComponent, { width: '520px', data })
					.afterClosed()
					.subscribe((result: JournalDescriptionEditDialogResult | undefined) => {
						if (!result?.saved) {
							return;
						}
						this.glService
							.updateJournalNarrative({
								journalNumber: Number(journal.journalNumber),
								narrative: result.narrative,
								updateReversal: result.updateReversal
							})
							.subscribe({
								next: (resp: any) => {
									const updated = Number(resp?.updated || 0);
									const updatedRev = Number(resp?.updatedReversal || 0);
									const msg = updatedRev > 0
										? `Updated ${updated} line(s) on Journal #${journal.journalNumber} and ${updatedRev} line(s) on the reversal.`
										: `Updated ${updated} line(s) on Journal #${journal.journalNumber}.`;
									this.globalSearchService.showNotification(msg, 'success', 'bottom', 'right');
									this.searchJournals();
								},
								error: () => {
									this.globalSearchService.showNotification(
										'Failed to update journal description.',
										'danger',
										'bottom',
										'right'
									);
								}
							});
					});
			},
			error: () => {
				this.globalSearchService.showNotification(
					'Failed to check reversal state.',
					'danger',
					'bottom',
					'right'
				);
			}
		});
	}

	loadReversal(journal: any): void {
		const transactions = journal.transactions || [];
		if (!transactions.length) {
			this.globalSearchService.showNotification('No lines to reverse.', 'danger', 'bottom', 'right');
			return;
		}
		const isReversalEntry = transactions.some((tx: any) =>
			(tx.description || '').trim().startsWith('Reversal of Journal #')
		);
		if (isReversalEntry) {
			this.globalSearchService.showNotification('Cannot reverse a reversal entry.', 'danger', 'bottom', 'right');
			return;
		}
		this.glService.checkJournalReversed(journal.journalNumber).subscribe((res: any) => {
			if (res.alreadyReversed && res.reversalJournalNumber) {
				this.globalSearchService.showNotification(
					'This journal has already been reversed (Journal #' + res.reversalJournalNumber + ').',
					'danger',
					'bottom',
					'right'
				);
				return;
			}
			const now = new Date().toISOString();
			const reversalLines = transactions.map((tx: any) => {
				const amountValue = String(tx.amount ?? '0').replace(/,/g, '');
				const amount = parseFloat(amountValue) || 0;
				return {
					account: { accountcode: tx.accountCode, accountname: tx.accountName },
					description: 'Reversal of Journal #' + journal.journalNumber + ': ' + (tx.description || ''),
					debitcredit: -amount,
					entryType: amount >= 0 ? 'credit' : 'debit',
					trandate: now,
					type: 'normal',
					department: null
				};
			});
			this.router.navigate(['/general-ledger/journal-entry'], {
				state: { reversalLines, reversalJournalNumber: journal.journalNumber }
			});
		});
	}

	back(): void {
		this.location.back()
	}

	openAttachments(journal: any): void {
		this.attachmentsJournal = journal;
		this.journalAttachmentsList = [];
		this.selectedAttachment = null;
		this.selectedAttachmentSafeUrl = null;
		this.attachmentsLoading = true;
		const typeno = parseInt(journal.journalNumber, 10);
		if (isNaN(typeno)) {
			this.attachmentsLoading = false;
			return;
		}
		this.glService.getJournalAttachments(typeno).subscribe({
			next: (res: any) => {
				this.journalAttachmentsList = (res && res.attachments) ? res.attachments : [];
				this.attachmentsLoading = false;
				if (this.journalAttachmentsList.length === 1) {
					const only = this.journalAttachmentsList[0];
					if (this.isPdf(only) || this.isImage(only)) {
						this.viewAttachment(only);
					}
				}
			},
			error: () => {
				this.attachmentsLoading = false;
				this.globalSearchService.showNotification('Failed to load attachments', 'danger', 'bottom', 'right');
			}
		});
	}

	closeAttachments(): void {
		this.attachmentsJournal = null;
		this.journalAttachmentsList = [];
		this.selectedAttachment = null;
		this.selectedAttachmentSafeUrl = null;
	}

	viewAttachment(att: any): void {
		this.selectedAttachment = att;
		this.selectedAttachmentSafeUrl = att
			? this.sanitizer.bypassSecurityTrustResourceUrl(this.getAttachmentViewUrl(att))
			: null;
	}

	clearAttachmentView(): void {
		this.selectedAttachment = null;
		this.selectedAttachmentSafeUrl = null;
	}

	isPdf(att: any): boolean {
		const mime = (att?.mime_type || '').toLowerCase();
		const name = (att?.filename || '').toLowerCase();
		return mime === 'application/pdf' || name.endsWith('.pdf');
	}

	isImage(att: any): boolean {
		const mime = (att?.mime_type || '').toLowerCase();
		return mime.startsWith('image/');
	}

	getAttachmentViewUrl(att: any): string {
		return att?.id != null ? this.glService.getJournalAttachmentDownloadUrl(att.id, true) : '';
	}

	getAttachmentDownloadUrl(att: any): string {
		return att?.id != null ? this.glService.getJournalAttachmentDownloadUrl(att.id, false) : '';
	}

	repostGl(): void {
		if (confirm('Are you sure you want to repost the General Ledger? This action may take several minutes and will affect all journal entries.')) {
			this.reposting = true;
			this.glService.repostGl().subscribe({
				next: (response) => {
					this.reposting = false;
					alert('General Ledger reposted successfully!');
					// Optionally refresh the current data
					this.searchJournals();
				},
				error: (error) => {
					this.reposting = false;
					console.error('Error reposting GL:', error);
					alert('Error reposting General Ledger. Please try again or contact support if the problem persists.');
				}
			});
		}
	}

}